import { useEffect, useMemo, useState } from "react";

const TILE_ASSETS = [
  "tile-01.svg",
  "tile-02.svg",
  "tile-03.svg",
  "tile-04.svg",
  "tile-05.svg",
  "tile-06.svg",
];

/* ACPL lifecycle stack — fixed diagonal slots; motion step matches slot gap (no overlap) */
const STACK_SCALE = 0.72;

const scalePx = (value) => Math.round(value * STACK_SCALE);

function buildStackConfig(frontBottom, backBottom, backRight, frontRight, count) {
  const bottomStep = (backBottom - frontBottom) / (count - 1);
  const rightStep = (frontRight - backRight) / (count - 1);

  const layouts = Array.from({ length: count }, (_, index) => ({
    bottom: Math.round(frontBottom + (count - 1 - index) * bottomStep),
    right: Math.round(backRight + index * rightStep),
    z: (index + 1) * 10,
  }));

  return {
    layouts,
    bottomStep,
    rightStep,
    forward: { x: Math.round(rightStep), y: Math.round(-bottomStep) },
    mid: { x: Math.round(rightStep * 0.5), y: Math.round(-bottomStep * 0.85) },
  };
}

const DESKTOP_CONFIG = buildStackConfig(
  scalePx(20),
  scalePx(370),
  scalePx(130),
  scalePx(520),
  TILE_ASSETS.length
);

const MOBILE_CONFIG = buildStackConfig(
  scalePx(0),
  scalePx(200),
  scalePx(10),
  scalePx(260),
  TILE_ASSETS.length
);

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function mapRange(value, inputRange, outputRange) {
  if (value <= inputRange[0]) return outputRange[0];

  for (let i = 1; i < inputRange.length; i += 1) {
    if (value <= inputRange[i]) {
      const span = inputRange[i] - inputRange[i - 1];
      if (span === 0) return outputRange[i];
      const t = (value - inputRange[i - 1]) / span;
      return outputRange[i - 1] + (outputRange[i] - outputRange[i - 1]) * t;
    }
  }

  return outputRange[outputRange.length - 1];
}

function getCardMotion(index, scrollProgress, config) {
  const total = TILE_ASSETS.length;
  const segment = 1 / total;
  const start = index * segment;
  const mid = start + segment * 0.375;
  const end = start + segment * 0.75;

  const x = mapRange(
    scrollProgress,
    [0, start, mid, end, 1],
    [0, 0, config.mid.x, config.forward.x, config.forward.x]
  );
  const y = mapRange(
    scrollProgress,
    [0, start, mid, end, 1],
    [0, 0, config.mid.y, config.forward.y, config.forward.y]
  );

  return { x, y };
}

function useStackScale() {
  const [mobile, setMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 480px)");
    const update = () => setMobile(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  return mobile;
}

export default function ServiceDeliverStack({ activeIndex = 0, scrollProgress = 0 }) {
  const mobile = useStackScale();
  const total = TILE_ASSETS.length;
  const safeIndex = clamp(activeIndex, 0, total - 1);
  const safeProgress = clamp(scrollProgress, 0, 1);
  const config = mobile ? MOBILE_CONFIG : DESKTOP_CONFIG;
  const assetBase = `${process.env.PUBLIC_URL}/assets/service-tiles`;

  const layouts = useMemo(() => config.layouts, [config]);

  return (
    <div className="service-scroll-icon-stack has-active" aria-hidden="true">
      {TILE_ASSETS.map((file, index) => {
        const layout = layouts[index];
        const isActive = safeIndex === index;
        const motion = getCardMotion(index, safeProgress, config);

        return (
          <div
            key={file}
            className={`service-scroll-stack-item service-scroll-stack-item--${index + 1}${isActive ? " is-active" : ""}`}
            style={{
              bottom: `${layout.bottom}px`,
              right: `${layout.right}px`,
              zIndex: layout.z,
              "--motion-x": `${motion.x}px`,
              "--motion-y": `${motion.y}px`,
            }}
          >
            <img
              src={`${assetBase}/${file}`}
              alt=""
              className="service-scroll-stack-image"
              draggable={false}
            />
          </div>
        );
      })}
    </div>
  );
}
